import { createContext, useContext, useState, ReactNode, useCallback } from "react";

export interface SubscriptionTier {
  id: string;
  name: string;
  monthlyCredits: number;
  pricePerMonth: number;
  popular?: boolean;
}

export interface CreditPack {
  id: string;
  credits: number;
  price: number;
  perCredit: number;
  popular?: boolean;
}

export const subscriptionTiers: SubscriptionTier[] = [
  { id: "tall", name: "Tall", monthlyCredits: 1000, pricePerMonth: 25 },
  { id: "grande", name: "Grande", monthlyCredits: 2000, pricePerMonth: 50, popular: true },
  { id: "venti", name: "Venti", monthlyCredits: Number.POSITIVE_INFINITY, pricePerMonth: 100 },
];

export const creditPacks: CreditPack[] = [
  { id: "pack-2000", credits: 2000, price: 50, perCredit: 0.0250 },
  { id: "pack-5000", credits: 5000, price: 100, perCredit: 0.0200 },
  { id: "pack-15000", credits: 15000, price: 250, perCredit: 0.0167 },
  { id: "pack-35000", credits: 35000, price: 500, perCredit: 0.0143, popular: true },
  { id: "pack-75000", credits: 75000, price: 1000, perCredit: 0.0133 },
  { id: "pack-250000", credits: 250000, price: 2500, perCredit: 0.0100 },
];

interface CreditsContextType {
  balance: number;
  monthlyAllowance: number;
  currentTier: SubscriptionTier | null;
  isLowCredits: (neededForNext?: number) => boolean;
  hasEnoughCredits: (amount: number) => boolean;
  deductCredits: (amount: number) => boolean;
  addCredits: (amount: number) => void;
  upgradeTier: (tier: SubscriptionTier) => void;
  purchasePack: (pack: CreditPack) => void;
}

const CreditsContext = createContext<CreditsContextType>({
  balance: 150,
  monthlyAllowance: 500,
  currentTier: subscriptionTiers[0],
  isLowCredits: () => false,
  hasEnoughCredits: () => true,
  deductCredits: () => true,
  addCredits: () => {},
  upgradeTier: () => {},
  purchasePack: () => {},
});

const LOW_CREDIT_THRESHOLD = 0.15; // warn at 15% remaining

export function CreditsProvider({ children }: { children: ReactNode }) {
  const [balance, setBalance] = useState(75);
  const [currentTier, setCurrentTier] = useState<SubscriptionTier>(subscriptionTiers[0]);

  const monthlyAllowance = currentTier.monthlyCredits;

  const isLowCredits = useCallback((neededForNext?: number) => {
    if (neededForNext && balance < neededForNext) return true;
    return balance / monthlyAllowance <= LOW_CREDIT_THRESHOLD;
  }, [balance, monthlyAllowance]);

  const hasEnoughCredits = useCallback((amount: number) => balance >= amount, [balance]);

  const deductCredits = useCallback((amount: number) => {
    if (balance < amount) return false;
    setBalance(prev => prev - amount);
    return true;
  }, [balance]);

  const addCredits = useCallback((amount: number) => {
    setBalance(prev => prev + amount);
  }, []);

  const upgradeTier = useCallback((tier: SubscriptionTier) => {
    const creditDiff = tier.monthlyCredits - currentTier.monthlyCredits;
    setCurrentTier(tier);
    if (creditDiff > 0) setBalance(prev => prev + creditDiff);
  }, [currentTier]);

  const purchasePack = useCallback((pack: CreditPack) => {
    setBalance(prev => prev + pack.credits);
  }, []);

  return (
    <CreditsContext.Provider value={{
      balance,
      monthlyAllowance,
      currentTier,
      isLowCredits,
      hasEnoughCredits,
      deductCredits,
      addCredits,
      upgradeTier,
      purchasePack,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

export const useCredits = () => useContext(CreditsContext);
